"use client";

// THE NEXT RELEASE, per person (2026-10-23). What is parked for the v1.1 launch (PR #321: the
// connection gate, the invite, the first walk, the connections and agents spaces, the folder sheet,
// the richer ground, the daily digest's send time) ships to innernet.live behind this one switch:
// a TEST ACCOUNT (lib/testers/accounts) or, since 2026-10-24, a FOUNDER sees `next` (who rides it is
// decided on the server, lib/release/server); everyone else sees `now`, exactly as before.
//
// How the two stand side by side until the press:
//   · a component the release rewrote keeps today's version beside it as `<Name>.v1.tsx` (copied
//     from main as it was), and the place that mounts it picks one with useNext();
//   · a smaller difference is a branch inline, on useNext();
//   · CSS the release changed is kept as ONE sheet: rules that differ are scoped with
//     `:where(:root[data-release="next"])` or `:where(:root:not([data-release="next"]))`, which
//     add no specificity, so each person's cascade is the one their release was written against.
//     The attribute is written on <html> before first paint (ReleaseMark) for the dashboard.
// At the press: delete every *.v1.* file and every `now` branch, and drop the scoped `now` rules.

import { createContext, useContext, useLayoutEffect, type ReactNode } from "react";

export type Release = "now" | "next";

/** the attribute on <html> the scoped CSS reads */
export const RELEASE_ATTR = "data-release";

const ReleaseContext = createContext<Release>("now");

export function ReleaseProvider({ release, children }: { release: Release; children: ReactNode }) {
  return (
    <ReleaseContext.Provider value={release}>
      {release === "next" && <ReleaseMark />}
      {children}
    </ReleaseContext.Provider>
  );
}

/** which release this person is on */
export function useRelease(): Release {
  return useContext(ReleaseContext);
}

/** true for a test account: the v1.1 experience */
export function useNext(): boolean {
  return useContext(ReleaseContext) === "next";
}

/** writes data-release="next" on <html> while the page is parsed, so the first paint is already right */
function ReleaseMark() {
  // a client-side arrival (no server html parsed) is marked here; the mark stays for the session
  useLayoutEffect(() => {
    document.documentElement.setAttribute(RELEASE_ATTR, "next");
  }, []);
  return (
    <script
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: `document.documentElement.setAttribute(${JSON.stringify(RELEASE_ATTR)},"next")` }}
    />
  );
}
